/**
 * GPT Prompt Usage Dashboard
 * now integrates aggregated metrics endpoints (GPTMetricsApi) for:
 *  - usage-series (global + perUser)
 *  - prompt-type-series & distribution
 *  - efficiency-series (completionPerPromptToken)
 *  - correlation-series (distinct correlationIds)
 *  - top-users (by cost)
 *  - document-usage (top docs)
 *  - summary (totals & distinct counts)
 * Falls back to client-side aggregation of the current events page when an aggregated endpoint
 * is unavailable or returns an error. Raw events table still sourced from paginated events API.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GPTEventsApi, GPTMetricsApi } from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { MetricCard, InsightCard, ProgressBar } from '../../components/analytics/SharedAnalyticsComponents';
import PdfExportButton from '../../components/analytics/PdfExportButton';
import { ReportGenerator } from '../../utils/ReportGenerator';

/** Utility formatters */
const formatCurrency = (v) => `$${(v || 0).toFixed(4)}`;
const formatTokens = (v) => (v || 0).toLocaleString();
const formatLatency = (ms) => `${ms?.toLocaleString()} ms`;

function buildDateKey(isoStr) {
  if (!isoStr) return 'unknown';
  try { return isoStr.slice(0, 10); } catch { return 'unknown'; }
}

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

const defaultRangeDays = 7;

export default function GptPromptEventsDashboard() {
  const { isInitialized } = useAuth();
  const [filters, setFilters] = useState(() => {
    const to = new Date();
    const from = new Date(Date.now() - defaultRangeDays * 24 * 60 * 60 * 1000);
    return {
      from: from.toISOString(),
      to: to.toISOString(),
      page: 0,
      size: 50,
      promptType: '',
      userId: '',
      conversationId: '',
      correlationId: '',
      documentId: ''
    };
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageData, setPageData] = useState(null); // holds raw response
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [aggregated, setAggregated] = useState({ loading: false, error: null, data: {} });
  const [monthlyBudget, setMonthlyBudget] = useState(() => {
    const saved = localStorage.getItem('gpt_monthly_budget');
    return saved ? parseFloat(saved) : 200;
  });
  const [editingBudget, setEditingBudget] = useState(false);

  const loadData = useCallback(async () => {
    if (!isInitialized) return;
    setLoading(true);
    setError(null);
    try {
      const res = await GPTEventsApi.getPromptEvents({
        ...filters,
        // only include numeric if provided
        userId: filters.userId ? Number(filters.userId) : undefined,
        conversationId: filters.conversationId ? Number(filters.conversationId) : undefined,
        documentId: filters.documentId ? Number(filters.documentId) : undefined,
        page: filters.page,
        size: filters.size,
        promptType: filters.promptType || undefined,
        correlationId: filters.correlationId || undefined,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to fetch events (${res.status}): ${text}`);
      }
      const data = await res.json();
      setPageData(data);
    } catch (e) {
      console.error('GPT events fetch error', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [filters, isInitialized]);

  const loadAggregated = useCallback(async () => {
    if (!isInitialized) return;
    const { from, to, userId, documentId, conversationId, promptType } = filters;
    const interval = (new Date(to) - new Date(from)) <= 48 * 3600 * 1000 ? 'hour' : 'day';
    const baseFilters = {
      from, to, interval,
      userId: userId ? Number(userId) : undefined,
      documentId: documentId ? Number(documentId) : undefined,
      conversationId: conversationId ? Number(conversationId) : undefined,
      promptTypes: promptType ? promptType.split(/[,\s]+/).filter(Boolean) : undefined,
    };
    setAggregated(a => ({ ...a, loading: true, error: null }));
    try {
      const [usageRes, usagePerUserRes, promptTypeSeriesRes, efficiencyRes, correlationRes, topUsersRes, promptTypeDistRes, docUsageRes, summaryRes] = await Promise.all([
        GPTMetricsApi.getUsageSeries({ ...baseFilters }),
        GPTMetricsApi.getUsageSeries({ ...baseFilters, perUser: true }),
        GPTMetricsApi.getPromptTypeSeries({ ...baseFilters }),
        GPTMetricsApi.getEfficiencySeries({ ...baseFilters }),
        GPTMetricsApi.getCorrelationSeries({ ...baseFilters }),
        GPTMetricsApi.getTopUsers({ ...baseFilters, limit: 10 }),
        GPTMetricsApi.getPromptTypeDistribution({ ...baseFilters }),
        GPTMetricsApi.getDocumentUsage({ ...baseFilters, limit: 15 }),
        GPTMetricsApi.getSummary({ ...baseFilters }),
      ]);
      const parse = async (r, fallback=[]) => r.ok ? r.json() : fallback;
      const data = {
        usageSeries: await parse(usageRes),
        usagePerUserSeries: await parse(usagePerUserRes),
        promptTypeSeries: await parse(promptTypeSeriesRes),
        efficiencySeries: await parse(efficiencyRes),
        correlationSeries: await parse(correlationRes),
        topUsers: await parse(topUsersRes),
        promptTypeDistribution: await parse(promptTypeDistRes),
        documentUsage: await parse(docUsageRes),
        summary: await parse(summaryRes, {}),
        interval,
      };
      setAggregated({ loading: false, error: null, data });
    } catch (e) {
      console.error('Aggregated metrics fetch error', e);
      setAggregated({ loading: false, error: e, data: {} });
    }
  }, [filters, isInitialized]);

  useEffect(() => { loadData(); }, [loadData, refreshIndex]);
  useEffect(() => { loadAggregated(); }, [loadAggregated, refreshIndex]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const items = pageData?.items || [];

  // Aggregations
  const aggregates = useMemo(() => {
    if (!items.length) return null;
    let totalCost = 0, totalTokens = 0, totalPromptTokens = 0, totalCompletionTokens = 0, totalLatency = 0;
    const byModel = {}; // model -> { count, cost, tokens }
    const byDay = {}; // date -> { date, cost, tokens, calls }
    const byPromptType = {}; // type -> { count, cost, tokens }
    const byDayUser = {}; // date -> userId -> { cost, tokens, promptTokens, completionTokens, calls }
    const byDayPromptType = {}; // date -> promptType -> { cost, tokens, promptTokens, completionTokens, calls }

    for (const ev of items) {
      totalCost += ev.costUsd || 0;
      totalTokens += ev.totalTokens || 0;
      totalPromptTokens += ev.promptTokens || 0;
      totalCompletionTokens += ev.completionTokens || 0;
      if (ev.latencyMs) totalLatency += ev.latencyMs;

      if (!byModel[ev.model]) byModel[ev.model] = { model: ev.model, count: 0, cost: 0, tokens: 0 };
      byModel[ev.model].count += 1;
      byModel[ev.model].cost += ev.costUsd || 0;
      byModel[ev.model].tokens += ev.totalTokens || 0;

      const dateKey = buildDateKey(ev.occurredAt || ev.createdAt);
      if (!byDay[dateKey]) byDay[dateKey] = { date: dateKey, cost: 0, tokens: 0, calls: 0 };
      byDay[dateKey].cost += ev.costUsd || 0;
      byDay[dateKey].tokens += ev.totalTokens || 0;
      byDay[dateKey].calls += 1;

      // Per-day per-user
      if (!byDayUser[dateKey]) byDayUser[dateKey] = {};
      const uKey = ev.userId || 'unknown';
      if (!byDayUser[dateKey][uKey]) byDayUser[dateKey][uKey] = { cost:0, tokens:0, promptTokens:0, completionTokens:0, calls:0 };
      byDayUser[dateKey][uKey].cost += ev.costUsd || 0;
      byDayUser[dateKey][uKey].tokens += ev.totalTokens || 0;
      byDayUser[dateKey][uKey].promptTokens += ev.promptTokens || 0;
      byDayUser[dateKey][uKey].completionTokens += ev.completionTokens || 0;
      byDayUser[dateKey][uKey].calls += 1;

      // Per-day per-promptType
      if (!byDayPromptType[dateKey]) byDayPromptType[dateKey] = {};
      const ptKey = (ev.promptType || 'unknown');
      if (!byDayPromptType[dateKey][ptKey]) byDayPromptType[dateKey][ptKey] = { cost:0, tokens:0, promptTokens:0, completionTokens:0, calls:0 };
      byDayPromptType[dateKey][ptKey].cost += ev.costUsd || 0;
      byDayPromptType[dateKey][ptKey].tokens += ev.totalTokens || 0;
      byDayPromptType[dateKey][ptKey].promptTokens += ev.promptTokens || 0;
      byDayPromptType[dateKey][ptKey].completionTokens += ev.completionTokens || 0;
      byDayPromptType[dateKey][ptKey].calls += 1;

      const pt = ev.promptType || 'unknown';
      if (!byPromptType[pt]) byPromptType[pt] = { promptType: pt, count: 0, cost: 0, tokens: 0 };
      byPromptType[pt].count += 1;
      byPromptType[pt].cost += ev.costUsd || 0;
      byPromptType[pt].tokens += ev.totalTokens || 0;
    }

    const avgLatency = items.length ? totalLatency / items.length : 0;

    // Transform per-user averages timeline
    const perUserSeries = Object.keys(byDayUser).sort().map(date => {
      const entry = { date };
      Object.entries(byDayUser[date]).forEach(([userId, stats]) => {
        // compute averages
        entry[`user_${userId}_avgCost`] = stats.calls ? stats.cost / stats.calls : 0;
        entry[`user_${userId}_avgTokens`] = stats.calls ? stats.tokens / stats.calls : 0;
        entry[`user_${userId}_avgPromptTokens`] = stats.calls ? stats.promptTokens / stats.calls : 0;
        entry[`user_${userId}_avgCompletionTokens`] = stats.calls ? stats.completionTokens / stats.calls : 0;
      });
      return entry;
    });

    const perPromptTypeSeries = Object.keys(byDayPromptType).sort().map(date => {
      const entry = { date };
      Object.entries(byDayPromptType[date]).forEach(([ptype, stats]) => {
        entry[`pt_${ptype}_avgCost`] = stats.calls ? stats.cost / stats.calls : 0;
        entry[`pt_${ptype}_avgTokens`] = stats.calls ? stats.tokens / stats.calls : 0;
        entry[`pt_${ptype}_avgPromptTokens`] = stats.calls ? stats.promptTokens / stats.calls : 0;
        entry[`pt_${ptype}_avgCompletionTokens`] = stats.calls ? stats.completionTokens / stats.calls : 0;
      });
      return entry;
    });

    return {
      totalCalls: items.length,
      totalCost,
      totalTokens,
      totalPromptTokens,
      totalCompletionTokens,
      avgLatency,
      byModel: Object.values(byModel).sort((a,b)=>b.count-a.count),
      byDay: Object.values(byDay).sort((a,b)=>a.date.localeCompare(b.date)),
      byPromptType: Object.values(byPromptType).sort((a,b)=>b.count-a.count),
      perUserSeries,
      perPromptTypeSeries,
      perUserKeys: perUserSeries.length ? Object.keys(perUserSeries[0]).filter(k=>k!=='date') : [],
      perPromptTypeKeys: perPromptTypeSeries.length ? Object.keys(perPromptTypeSeries[0]).filter(k=>k!=='date') : []
    };
  }, [items]);

  const [userMetric, setUserMetric] = useState('avgCost');
  const [promptTypeMetric, setPromptTypeMetric] = useState('avgCost');

  const buildLines = (keys, metric) => {
    // keys like user_1_avgCost; filter by selected metric suffix
    return keys.filter(k=>k.endsWith(metric)).map((k,i)=>(
      <Line key={k} type="monotone" dataKey={k} name={k.replace(/^user_|^pt_/,'').replace(/_avg/,'')} stroke={COLORS[i % COLORS.length]} dot={false} strokeWidth={2} />
    ));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(f => ({ ...f, [name]: value, page: name === 'size' ? f.page : 0 }));
  };

  const handlePageChange = (delta) => {
    setFilters(f => ({ ...f, page: Math.max(0, (f.page || 0) + delta) }));
  };

  const handleDateQuick = (days) => {
    const to = new Date();
    const from = new Date(Date.now() - days * 24 * 3600 * 1000);
    setFilters(f => ({ ...f, from: from.toISOString(), to: to.toISOString(), page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters(f => ({ ...f, userId:'', conversationId:'', correlationId:'', documentId:'', promptType:'', page:0 }));
  };

  const reload = () => setRefreshIndex(i => i + 1);
  const chartsRef = useRef(null);

  const handlePdfExport = useCallback(async () => {
    const report = new ReportGenerator('GPT Prompt Usage Report', { from: filters.from, to: filters.to });
    if (aggregates) {
      report.addMetrics([
        { label: 'Total Cost', value: formatCurrency(aggregates.totalCost) },
        { label: 'Total Calls', value: aggregates.totalCalls?.toLocaleString() || '0' },
        { label: 'Total Tokens', value: formatTokens(aggregates.totalTokens) },
        { label: 'Avg Latency', value: formatLatency(aggregates.avgLatency) },
      ]);
    }
    if (chartsRef.current) {
      report.addSectionHeading('Cost & Usage Trends');
      await report.addChartFromRef(chartsRef, 'GPU cost and usage charts');
    }
    if (aggregates?.byModel?.length) {
      report.addSectionHeading('Usage by Model');
      report.addTable(
        ['Model', 'Calls', 'Cost', 'Tokens', 'Avg Latency'],
        aggregates.byModel.map(m => [m.model, m.count, formatCurrency(m.cost), formatTokens(m.tokens), `${m.avgLatency?.toFixed(0) || 0} ms`])
      );
    }
    if (aggregates?.byPromptType?.length) {
      report.addSectionHeading('Usage by Prompt Type');
      report.addTable(
        ['Prompt Type', 'Calls', 'Cost', 'Tokens'],
        aggregates.byPromptType.map(p => [p.promptType, p.count, formatCurrency(p.cost), formatTokens(p.tokens)])
      );
    }
    report.download('gpt-prompt-usage');
  }, [aggregates, filters.from, filters.to]);

  return (
    <div className="settings-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>GPT Prompt Usage</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={reload} disabled={loading}>{loading ? 'Loading...' : '🔄 Refresh'}</button>
          <button className="secondary" onClick={() => handleDateQuick(1)}>Last 24h</button>
          <button className="secondary" onClick={() => handleDateQuick(7)}>7d</button>
          <button className="secondary" onClick={() => handleDateQuick(30)}>30d</button>
          <button className="secondary" onClick={handleClearFilters}>Clear Filters</button>
          <PdfExportButton onGenerate={handlePdfExport} disabled={!aggregates} />
        </div>
      </div>

      <div style={{ marginTop: '1rem', background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <form onSubmit={e => { e.preventDefault(); loadData(); }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>From (ISO)</label>
            <input name="from" value={filters.from} onChange={handleFilterChange} style={{ width: '100%' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>To (ISO)</label>
            <input name="to" value={filters.to} onChange={handleFilterChange} style={{ width: '100%' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>User ID</label>
            <input name="userId" value={filters.userId} onChange={handleFilterChange} />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>Conversation ID</label>
            <input name="conversationId" value={filters.conversationId} onChange={handleFilterChange} />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>Document ID</label>
            <input name="documentId" value={filters.documentId} onChange={handleFilterChange} />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>Correlation ID</label>
            <input name="correlationId" value={filters.correlationId} onChange={handleFilterChange} />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>Prompt Type</label>
            <input name="promptType" value={filters.promptType} onChange={handleFilterChange} placeholder="conversation, ..." />
          </div>
          <div>
            <label style={{ fontSize: '0.7rem', fontWeight: 600 }}>Page Size</label>
            <select name="size" value={filters.size} onChange={handleFilterChange}>
              {[25,50,100,250].map(s=> <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ display:'flex', alignItems:'flex-end' }}>
            <button type="submit" style={{ width:'100%' }}>Apply</button>
          </div>
        </form>
      </div>

      {error && <div className="error-message" style={{ marginTop:'1rem' }}>{error.message}</div>}
      {aggregated.loading && <div style={{ marginTop:'1rem' }}>Loading aggregated metrics...</div>}
      {aggregated.error && <div style={{ marginTop:'1rem', color:'var(--danger,#c00)' }}>Aggregated metrics error: {aggregated.error.message}</div>}

      {/* KPI Cards — powered by server-side summary (full time range), NOT the current page */}
      {(aggregated.data.summary?.prompts > 0 || (aggregates && !loading)) && (() => {
        const s = aggregated.data.summary;
        const hasSummary = s && s.prompts > 0;
        const totalCost = hasSummary ? Number(s.costUsd || 0) : aggregates?.totalCost || 0;
        const totalCalls = hasSummary ? s.prompts : aggregates?.totalCalls || 0;
        const totalTokens = hasSummary ? s.totalTokens : aggregates?.totalTokens || 0;
        const totalPromptTokens = hasSummary ? s.promptTokens : aggregates?.totalPromptTokens || 0;
        const totalCompletionTokens = hasSummary ? s.completionTokens : aggregates?.totalCompletionTokens || 0;
        const distinctUsers = hasSummary ? s.distinctUsers : null;
        const distinctDocs = hasSummary ? s.distinctDocuments : null;
        return (
        <>
          {/* Budget Warning if cost is high */}
          {totalCost > 50 && (
            <div style={{ marginTop:'1rem' }}>
              <InsightCard
                type={totalCost > 100 ? 'danger' : 'warning'}
                title={`Budget Alert: $${totalCost.toFixed(2)} spent`}
                message={`AI usage has cost $${totalCost.toFixed(2)} in this period. ${totalCost > 100 ? 'Consider setting stricter limits.' : 'Monitor usage closely.'}`}
              />
            </div>
          )}
          
          {/* Enhanced KPI Cards — full time-range totals */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '1rem', marginTop: '1rem' }}>
            <MetricCard 
              label="Total Cost" 
              value={formatCurrency(totalCost)}
              subtitle={`${totalCalls.toLocaleString()} calls`}
              sparklineData={(aggregates?.byDay || []).map(d => ({ value: d.cost || 0 }))}
              trend={(aggregates?.byDay || []).length > 1 ? 
                ((aggregates.byDay[aggregates.byDay.length-1]?.cost - aggregates.byDay[0]?.cost) / (aggregates.byDay[0]?.cost || 1) * 100) : 0
              }
              color="#dc3545"
            />
            <MetricCard 
              label="Total Tokens" 
              value={formatTokens(totalTokens)}
              subtitle={`${formatTokens(totalPromptTokens)} prompt + ${formatTokens(totalCompletionTokens)} completion`}
              sparklineData={(aggregates?.byDay || []).map(d => ({ value: (d.promptTokens || 0) + (d.completionTokens || 0) }))}
              color="#007bff"
            />
            <MetricCard 
              label="Cost Efficiency" 
              value={totalCalls > 0 ? `$${(totalCost / totalCalls).toFixed(4)}/call` : '-'}
              subtitle={totalCalls > 0 ? `${(totalTokens / totalCalls).toFixed(0)} tokens/call` : ''}
              color="#ffc107"
            />
            {distinctUsers != null && (
              <MetricCard 
                label="Active Users" 
                value={distinctUsers.toLocaleString()}
                subtitle={`${(distinctDocs || 0).toLocaleString()} documents`}
                color="#28a745"
              />
            )}
            {!hasSummary && (
              <MetricCard 
                label="Avg Latency" 
                value={formatLatency((aggregates?.avgLatency || 0).toFixed(0))}
                subtitle={`across ${totalCalls.toLocaleString()} calls`}
                color="#28a745"
              />
            )}
          </div>
          
          {/* Budget Progress Bar */}
          <div style={{ marginTop:'1rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border:'1px solid var(--border-light)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
              <h4 style={{ margin:0, fontSize:'.9rem' }}>Budget Usage</h4>
              <div style={{ display:'flex', gap:'.5rem', alignItems:'center' }}>
                {editingBudget ? (
                  <>
                    <span style={{ fontSize:'.8rem' }}>$</span>
                    <input
                      type="number"
                      value={monthlyBudget}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setMonthlyBudget(val);
                        localStorage.setItem('gpt_monthly_budget', String(val));
                      }}
                      style={{ width:'80px', fontSize:'.8rem' }}
                      min={0}
                      step={10}
                    />
                    <button onClick={() => setEditingBudget(false)} style={{ fontSize:'.75rem', padding:'.2rem .5rem' }}>✓</button>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize:'.85rem', color:'var(--text-secondary)' }}>${totalCost.toFixed(2)} / ${monthlyBudget.toFixed(2)}</span>
                    <button onClick={() => setEditingBudget(true)} style={{ fontSize:'.7rem', padding:'.15rem .4rem' }}>✏️</button>
                  </>
                )}
              </div>
            </div>
            <ProgressBar value={totalCost} max={monthlyBudget} />
          </div>
        </>
        );
      })()}

      {/* Charts */}
      {aggregates && !!aggregates.byDay.length && (
        <div ref={chartsRef} style={{ marginTop: '1.5rem', display: 'grid', gap: '1.5rem', gridTemplateColumns: 'minmax(0,1fr)' }}>
          <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
            <h3 style={{ marginTop:0 }}>Cost & Calls by Day</h3>
            {/* If aggregated usageSeries available, prefer it */}
            <div style={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={(aggregated.data.usageSeries?.length ? aggregated.data.usageSeries.map(d=>({ date: d.bucket?.slice(0,10), cost: d.costUsd, calls: d.prompts })) : aggregates.byDay)} margin={{ top:5, right:20, left:0, bottom:0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip formatter={(val, name)=> name==='cost'?formatCurrency(val):val} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="cost" name="cost" stroke="#007bff" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="calls" name="calls" stroke="#28a745" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {aggregates.perUserSeries?.length > 0 && (
            <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem' }}>
                <h3 style={{ margin:0 }}>Per-User Averages Over Time</h3>
                <select value={userMetric} onChange={e=>setUserMetric(e.target.value)}>
                  <option value="avgCost">Avg Cost</option>
                  <option value="avgTokens">Avg Total Tokens</option>
                  <option value="avgPromptTokens">Avg Prompt Tokens</option>
                  <option value="avgCompletionTokens">Avg Completion Tokens</option>
                </select>
              </div>
              <div style={{ width:'100%', height:300 }}>
                <ResponsiveContainer>
                  <LineChart data={aggregates.perUserSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {buildLines(aggregates.perUserKeys, userMetric)}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {aggregates.perPromptTypeSeries?.length > 0 && (
            <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.5rem' }}>
                <h3 style={{ margin:0 }}>Per-Prompt Type Averages Over Time</h3>
                <select value={promptTypeMetric} onChange={e=>setPromptTypeMetric(e.target.value)}>
                  <option value="avgCost">Avg Cost</option>
                  <option value="avgTokens">Avg Total Tokens</option>
                  <option value="avgPromptTokens">Avg Prompt Tokens</option>
                  <option value="avgCompletionTokens">Avg Completion Tokens</option>
                </select>
              </div>
              <div style={{ width:'100%', height:300 }}>
                <ResponsiveContainer>
                  <LineChart data={aggregates.perPromptTypeSeries}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {buildLines(aggregates.perPromptTypeKeys, promptTypeMetric)}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:'1.5rem' }}>
            <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <h3 style={{ marginTop:0 }}>Usage by Model (Calls)</h3>
              <div style={{ width:'100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={aggregates.byModel}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="model" hide={aggregates.byModel.length > 6} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" name="Calls" fill="#007bff" />
                    <Bar dataKey="tokens" name="Tokens" fill="#17a2b8" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={{ background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
              <h3 style={{ marginTop:0 }}>Prompt Type Distribution</h3>
              <div style={{ width:'100%', height: 300 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie dataKey="prompts" data={(aggregated.data.promptTypeDistribution?.length ? aggregated.data.promptTypeDistribution : aggregates.byPromptType)} nameKey="promptType" outerRadius={100} label>
                      {(aggregated.data.promptTypeDistribution?.length ? aggregated.data.promptTypeDistribution : aggregates.byPromptType).map((e,i)=> <Cell key={e.promptType ?? 'null'} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Aggregated-only sections */}
      {aggregated.data.efficiencySeries?.length > 0 && (
        <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop:0 }}>Efficiency Series (Completion / Prompt Token)</h3>
          <div style={{ width:'100%', height:280 }}>
            <ResponsiveContainer>
              <LineChart data={aggregated.data.efficiencySeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tickFormatter={v=>v?.slice(0,10)} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="completionPerPromptToken" name="completionPerPromptToken" stroke="#6f42c1" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {aggregated.data.correlationSeries?.length > 0 && (
        <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop:0 }}>Distinct Correlations Over Time</h3>
          <div style={{ width:'100%', height:280 }}>
            <ResponsiveContainer>
              <LineChart data={aggregated.data.correlationSeries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" tickFormatter={v=>v?.slice(0,10)} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="distinctCorrelationIds" name="distinctCorrelationIds" stroke="#20c997" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {aggregated.data.topUsers?.length > 0 && (
        <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop:0 }}>Top Users (By Cost)</h3>
          <div style={{ width:'100%', height:320 }}>
            <ResponsiveContainer>
              <BarChart data={aggregated.data.topUsers}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="userId" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="costUsd" name="Cost" fill="#fd7e14" />
                <Bar dataKey="prompts" name="Prompts" fill="#28a745" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {aggregated.data.documentUsage?.length > 0 && (
        <div style={{ marginTop:'1.5rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop:0 }}>Document Usage (Top)</h3>
          <div style={{ width:'100%', height:320 }}>
            <ResponsiveContainer>
              <BarChart data={aggregated.data.documentUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="documentId" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="costUsd" name="Cost" fill="#dc3545" />
                <Bar dataKey="prompts" name="Prompts" fill="#007bff" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ marginTop: '2rem', background:'var(--card-bg)', padding:'1rem', borderRadius:'12px', boxShadow:'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <h3 style={{ marginTop:0, marginBottom: '0.5rem' }}>Raw Events</h3>
        {loading && <div className="loading-state"><div className="loading-spinner" /> Loading events...</div>}
        {!loading && items.length === 0 && <div className="empty-state">No events for selected filters.</div>}
        {!loading && items.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <EventsTableGrouped items={items} />
          </div>
        )}
        <div style={{ marginTop: '0.75rem', display:'flex', justifyContent:'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ fontSize:'0.75rem', color:'#555' }}>
            Page {filters.page + 1} of {pageData?.totalPages || 1} | Total Elements: {pageData?.totalElements?.toLocaleString?.()}
          </div>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button className="secondary" disabled={filters.page === 0 || loading} onClick={()=>handlePageChange(-1)}>Prev</button>
            <button className="secondary" disabled={loading || (pageData && !pageData.hasNext)} onClick={()=>handlePageChange(1)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * EventsTableGrouped
 * Groups events by correlationId and displays aggregate stats per group.
 * Makes correlationId clickable to view detailed correlation timeline.
 */
function EventsTableGrouped({ items }) {
  // Group events by correlationId
  const grouped = useMemo(() => {
    const groups = {};
    items.forEach(ev => {
      const key = ev.correlationId || 'no-correlation';
      if (!groups[key]) {
        groups[key] = {
          correlationId: ev.correlationId,
          events: [],
          totalCalls: 0,
          totalPromptTokens: 0,
          totalCompletionTokens: 0,
          totalTokens: 0,
          totalCost: 0,
          avgLatency: 0,
          firstOccurred: ev.occurredAt || ev.createdAt,
          lastOccurred: ev.occurredAt || ev.createdAt,
          users: new Set(),
          promptTypes: new Set(),
          models: new Set(),
          documents: new Set()
        };
      }
      const group = groups[key];
      group.events.push(ev);
      group.totalCalls++;
      group.totalPromptTokens += ev.promptTokens || 0;
      group.totalCompletionTokens += ev.completionTokens || 0;
      group.totalTokens += ev.totalTokens || 0;
      group.totalCost += ev.costUsd || 0;
      group.avgLatency += ev.latencyMs || 0;
      if (ev.userId) group.users.add(ev.userId);
      if (ev.promptType) group.promptTypes.add(ev.promptType);
      if (ev.model) group.models.add(ev.model);
      if (ev.documentId) group.documents.add(ev.documentId);
      
      const occurred = new Date(ev.occurredAt || ev.createdAt);
      if (occurred < new Date(group.firstOccurred)) group.firstOccurred = ev.occurredAt || ev.createdAt;
      if (occurred > new Date(group.lastOccurred)) group.lastOccurred = ev.occurredAt || ev.createdAt;
    });
    
    // Calculate averages and convert Sets to counts
    Object.values(groups).forEach(group => {
      group.avgLatency = group.totalCalls > 0 ? group.avgLatency / group.totalCalls : 0;
      group.userCount = group.users.size;
      group.promptTypeCount = group.promptTypes.size;
      group.modelCount = group.models.size;
      group.documentCount = group.documents.size;
    });
    
    // Sort by most recent first
    return Object.values(groups).sort((a, b) => 
      new Date(b.lastOccurred) - new Date(a.lastOccurred)
    );
  }, [items]);

  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const toggleGroup = (correlationId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(correlationId)) {
        newSet.delete(correlationId);
      } else {
        newSet.add(correlationId);
      }
      return newSet;
    });
  };

  return (
    <table>
      <thead>
        <tr>
          <th style={{ width: '30px' }}></th>
          <th>Correlation ID</th>
          <th>Calls</th>
          <th>Total Tokens</th>
          <th>Total Cost</th>
          <th>Avg Latency</th>
          <th>Time Range</th>
          <th>Types</th>
          <th>Models</th>
          <th>Docs</th>
        </tr>
      </thead>
      <tbody>
        {grouped.map(group => (
          <React.Fragment key={group.correlationId || 'no-correlation'}>
            {/* Group Header Row */}
            <tr style={{ 
              background: group.correlationId ? 'var(--row-hover)' : '#f9f9f9',
              fontWeight: 600,
              cursor: 'pointer'
            }}
            onClick={() => group.correlationId && toggleGroup(group.correlationId)}>
              <td>
                {group.correlationId && (
                  <span style={{ fontSize: '0.8rem' }}>
                    {expandedGroups.has(group.correlationId) ? '▼' : '▶'}
                  </span>
                )}
              </td>
              <td>
                {group.correlationId ? (
                  <Link 
                    to={`/settings/correlation/${group.correlationId}`}
                    style={{ 
                      color: 'var(--primary)', 
                      textDecoration: 'none',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }}
                    title={group.correlationId}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {group.correlationId.substring(0, 8)}...{group.correlationId.substring(group.correlationId.length - 4)}
                  </Link>
                ) : (
                  <span style={{ color: '#999', fontStyle: 'italic' }}>No Correlation</span>
                )}
              </td>
              <td>{group.totalCalls}</td>
              <td>{formatTokens(group.totalTokens)}</td>
              <td style={{ color: group.totalCost > 0 ? '#dc3545' : 'inherit' }}>
                {formatCurrency(group.totalCost)}
              </td>
              <td>{formatLatency(Math.round(group.avgLatency))}</td>
              <td style={{ fontSize: '0.75rem' }}>
                {new Date(group.firstOccurred).toLocaleString()}
              </td>
              <td>{group.promptTypeCount}</td>
              <td>{group.modelCount}</td>
              <td>{group.documentCount}</td>
            </tr>
            
            {/* Expanded Detail Rows */}
            {group.correlationId && expandedGroups.has(group.correlationId) && (
              <tr>
                <td colSpan="10" style={{ padding: 0, background: '#f8f9fa' }}>
                  <div style={{ padding: '0.5rem 1rem' }}>
                    <table style={{ width: '100%', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: 'transparent' }}>
                          <th>Time</th>
                          <th>User</th>
                          <th>Type</th>
                          <th>Model</th>
                          <th>Tokens (P/C/T)</th>
                          <th>Latency</th>
                          <th>Cost</th>
                          <th>Doc</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.events.map(ev => (
                          <tr key={ev.id}>
                            <td>{new Date(ev.occurredAt || ev.createdAt).toLocaleString()}</td>
                            <td>{ev.userId}</td>
                            <td>{ev.promptType}</td>
                            <td>{ev.model}</td>
                            <td>{ev.promptTokens}/{ev.completionTokens}/{ev.totalTokens}</td>
                            <td>{formatLatency(ev.latencyMs)}</td>
                            <td>{formatCurrency(ev.costUsd)}</td>
                            <td>{ev.documentId || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

